/** Client helpers for Product Finder result filtering (pure). */

export const MODE_GUIDANCE = {
  quick: "Search the most relevant sources first.",
  deep: "Search all compatible operational national sources. This may take several minutes.",
  regional: "Search nearby regional stores using your ZIP code.",
};

export const TERMINAL_STATUSES = new Set([
  "completed",
  "completed_with_warnings",
  "failed",
  "cancelled",
  "no_operational_sources",
]);

export function isTerminalStatus(status) {
  return TERMINAL_STATUSES.has(String(status || ""));
}

export function offerKey(offer, bucket, index) {
  return (
    offer?.offer_id ||
    offer?.id ||
    `${bucket}:${index}`
  );
}

/**
 * Filter offers without changing match_status.
 * @param {object[]} offers
 * @param {object} filters
 */
export function filterOffers(offers, filters = {}) {
  const list = Array.isArray(offers) ? offers : [];
  return list.filter((o) => {
    if (filters.verifiedOnly && o.match_status !== "verified") return false;
    if (filters.inStock) {
      const a = String(o.availability || "").toLowerCase();
      if (!(a.includes("in_stock") || a.includes("instock") || a.includes("in stock") || a === "available")) {
        return false;
      }
    }
    if (filters.fulfillment) {
      if (String(o.fulfillment || "").toLowerCase() !== String(filters.fulfillment).toLowerCase()) {
        return false;
      }
    }
    if (filters.positiveRoi) {
      if (!(typeof o.roi === "number" && o.roi > 0)) return false;
    }
    if (filters.minimumRoi != null && filters.minimumRoi !== "") {
      const min = Number(filters.minimumRoi);
      if (Number.isFinite(min)) {
        if (o.roi == null || o.roi < min) return false;
      }
    }
    if (filters.source) {
      const s = String(filters.source).toLowerCase();
      const hay = `${o.source_id || ""} ${o.source_name || ""}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    if (filters.minPrice != null && filters.minPrice !== "") {
      const minP = Number(filters.minPrice);
      if (Number.isFinite(minP) && (o.source_price == null || o.source_price < minP)) return false;
    }
    if (filters.maxPrice != null && filters.maxPrice !== "") {
      const maxP = Number(filters.maxPrice);
      if (Number.isFinite(maxP) && (o.source_price == null || o.source_price > maxP)) return false;
    }
    if (filters.freshWithinHours != null && filters.freshWithinHours !== "") {
      const hrs = Number(filters.freshWithinHours);
      if (Number.isFinite(hrs) && o.last_checked_at) {
        const ageH = (Date.now() - new Date(o.last_checked_at).getTime()) / 3600000;
        if (ageH > hrs) return false;
      }
    }
    return true;
  });
}

export function formatEconomics(offer) {
  if (!offer) return { kind: "missing" };
  const profit = offer.estimated_profit;
  const roi = offer.roi;
  if (profit == null && roi == null) {
    return { kind: "missing", label: "Economics unavailable" };
  }
  if (typeof profit === "number" && profit < 0) {
    return {
      kind: "loss",
      profit,
      roi,
      label: `Estimated loss`,
    };
  }
  if (typeof roi === "number" && roi < 0) {
    return { kind: "loss", profit, roi, label: "Estimated loss" };
  }
  return { kind: "gain", profit, roi, label: "Estimated profit" };
}

export function validateFinderForm(form) {
  const errors = [];
  const title = (form.title || "").trim();
  const identifier = (form.identifier || "").trim();
  const walmartUrl = (form.walmartUrl || "").trim();
  const walmartItemId = (form.walmartItemId || "").trim();
  if (!title && !identifier && !walmartUrl && !walmartItemId) {
    errors.push("Enter a product title, UPC/EAN/GTIN, Walmart URL, or item ID.");
  }
  if (form.searchMode === "regional") {
    const zip = String(form.zipCode || "").trim();
    if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      errors.push("Regional search requires a valid ZIP code.");
    }
  }
  return errors;
}

export function buildSearchPayload(form) {
  const payload = {
    query: (form.title || "").trim() || null,
    identifier: (form.identifier || "").trim() || null,
    identifier_type: form.identifierType || null,
    search_mode: form.searchMode || "quick",
    zip_code: form.searchMode === "regional" ? (form.zipCode || null) : null,
    radius_miles:
      form.searchMode === "regional" && form.radiusMiles !== "" && form.radiusMiles != null
        ? Number(form.radiusMiles)
        : null,
    max_sources: form.maxSources ? Number(form.maxSources) : null,
    minimum_roi:
      form.minimumRoi !== "" && form.minimumRoi != null ? Number(form.minimumRoi) : null,
    walmart_url: (form.walmartUrl || "").trim() || null,
    walmart_item_id: (form.walmartItemId || "").trim() || null,
    allow_ai_review: Boolean(form.allowAiReview),
    allow_provider_escalation: Boolean(form.allowProviderEscalation),
  };
  if (payload.identifier && !payload.identifier_type) {
    const digits = payload.identifier.replace(/\D/g, "");
    if (/^\d{12}$/.test(digits)) payload.identifier_type = "upc";
    else if (/^\d{13}$/.test(digits)) payload.identifier_type = "ean";
    else if (/^\d{8}$|^\d{14}$/.test(digits)) payload.identifier_type = "gtin";
    else if (/^\d{3,15}$/.test(digits)) payload.identifier_type = "walmart_item_id";
  }
  return payload;
}
